import {
  DashboardConversationFeedItemType,
  type DashboardMessageFeedItem,
  type MessageData,
} from '@fluenceur/types';
import type { QueryClient } from '@tanstack/react-query';
import type { ConversationMessagesInfiniteData } from './use-conversation-messages-query';
import { conversationQueryKeys } from './conversation-query-keys';

export const appendOptimisticMessage = (
  previous: ConversationMessagesInfiniteData | undefined,
  message: DashboardMessageFeedItem
): ConversationMessagesInfiniteData => {
  const firstPage = previous?.pages[0];
  if (!firstPage) {
    return {
      pageParams: [1],
      pages: [
        {
          data: [message],
          meta: { total: 1, perPage: 25, currentPage: 1, lastPage: 1, from: 1, to: 1 },
        },
      ],
    };
  }

  const pages = previous.pages.map((page, index) => {
    if (index !== 0) return page;
    const exists = page.data.some(
      (existing) =>
        existing.itemType === DashboardConversationFeedItemType.MESSAGE &&
        existing.clientMessageId === message.clientMessageId
    );
    return exists
      ? page
      : {
          ...page,
          data: [message, ...page.data],
          meta: { ...page.meta, total: page.meta.total + 1 },
        };
  });
  return { ...previous, pages };
};

export const replaceOptimisticMessage = (
  previous: ConversationMessagesInfiniteData | undefined,
  message: DashboardMessageFeedItem
): ConversationMessagesInfiniteData | undefined => {
  if (!previous) return previous;
  const pages = previous.pages.map((page) => ({
    ...page,
    data: page.data.map((existing) =>
      existing.itemType === DashboardConversationFeedItemType.MESSAGE &&
      existing.clientMessageId === message.clientMessageId
        ? message
        : existing
    ),
  }));
  return { ...previous, pages };
};

export const updateOptimisticMessageState = (
  previous: ConversationMessagesInfiniteData | undefined,
  clientMessageId: string,
  deliveryState: MessageData['deliveryState']
): ConversationMessagesInfiniteData | undefined => {
  if (!previous) return previous;
  return {
    ...previous,
    pages: previous.pages.map((page) => ({
      ...page,
      data: page.data.map((item) =>
        item.itemType === DashboardConversationFeedItemType.MESSAGE &&
        item.clientMessageId === clientMessageId
          ? { ...item, deliveryState }
          : item
      ),
    })),
  };
};

export const reconcileBrandInboxRealtimeEvent = (
  queryClient: QueryClient,
  brandId: string,
  _event: { conversationId: string; messageId?: string }
) => {
  queryClient.invalidateQueries({
    queryKey: conversationQueryKeys.all(brandId),
  });
};
